import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import SecureLS from "secure-ls";
import StoreTitle from "../../component/StoreTitle";
import {
  canCountInventory,
  canManageInventory,
  canRestockInventory,
} from "../../utils/inventoryPermissions";
import { useInventoryModuleStatus } from "./context/InventoryModuleStatusContext";

const ls = new SecureLS({ encodingType: "aes" });

const INVENTORY_TABS = [
  {
    path: "/inventory",
    label: "Inventory",
    description: "Items and supplies",
    icon: <Inventory2OutlinedIcon />,
  },
  {
    path: "/inventory/daily-stock",
    label: "Daily Stock",
    description: "Opening and closing counts",
    icon: <FactCheckOutlinedIcon />,
  },
  {
    path: "/inventory/restock",
    label: "Restock",
    description: "Replenish ingredients",
    icon: <LocalShippingOutlinedIcon />,
  },
  {
    path: "/inventory/archive",
    label: "Archive",
    description: "Inactive stock items",
    icon: <ArchiveOutlinedIcon />,
  },
  {
    path: "/inventory/history",
    label: "History",
    description: "Past stock changes",
    icon: <HistoryOutlinedIcon />,
  },
];

const InventoryHero = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { dailyTabStatus, overdueEndingYesterday } =
    useInventoryModuleStatus();
  const user = ls.get("user");
  const hasFullInventoryAccess = canManageInventory(user);
  const hasCountingAccess = canCountInventory(user);
  const hasRestockAccess = canRestockInventory(user);
  const visibleTabs = INVENTORY_TABS.filter(({ path }) => {
    if (hasFullInventoryAccess) return true;
    if (path === "/inventory/daily-stock") return hasCountingAccess;
    if (path === "/inventory/restock") return hasRestockAccess;
    return false;
  });
  const activePath =
    visibleTabs.find(({ path }) => path === pathname)?.path ||
    visibleTabs[0]?.path ||
    pathname;

  return (
    <Box component="header" sx={{ pb: { xs: 3, sm: 4 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(120px, 1fr) auto minmax(120px, 1fr)" },
          alignItems: "center",
          gap: { xs: 2, md: 3 },
          mb: { xs: 2.5, sm: 3 },
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard")}
          sx={{
            width: { xs: "100%", sm: "fit-content" },
            minHeight: 44,
            justifySelf: { xs: "stretch", sm: "start" },
          }}
        >
          Back to Dashboard
        </Button>
        <Box sx={{ minWidth: 0 }}>
          <StoreTitle
            title="Inventory Management"
            titleStyle={{ fontSize: "clamp(1.75rem, 4vw, 2.25rem)" }}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }} />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "background.paper",
          boxShadow: "0 8px 24px rgba(45, 24, 63, 0.06)",
        }}
      >
        <Tabs
          value={activePath}
          onChange={(_, path) => navigate(path)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Inventory sections"
          sx={{
            px: { xs: 0.5, sm: 1 },
            "& .MuiTabs-indicator": {
              height: 4,
              borderRadius: "4px 4px 0 0",
            },
            "& .MuiTabs-flexContainer": {
              gap: 0.5,
            },
            "& .MuiTab-root": {
              flex: { md: "1 1 0" },
              minWidth: { xs: 168, sm: 190, md: 0 },
              minHeight: 76,
              alignItems: "center",
              borderRadius: 2,
              my: 0.75,
              px: { xs: 1.5, sm: 2 },
              textTransform: "none",
              transition: "background-color 160ms ease, color 160ms ease",
            },
            "& .MuiTab-root.Mui-selected": {
              bgcolor: "action.selected",
            },
          }}
        >
          {visibleTabs.map(({ path, label, description, icon }) => {
            const isDailyStock = path === "/inventory/daily-stock";
            const statusDescription = isDailyStock
              ? dailyTabStatus
              : description;
            const hasPendingToday =
              isDailyStock &&
              statusDescription.toLowerCase().includes("not yet accomplished");
            const overdueWarning =
              isDailyStock && overdueEndingYesterday
                ? "Ending yesterday not yet accomplished"
                : "";

            return (
              <Tab
                key={path}
                value={path}
                icon={icon}
                iconPosition="start"
                aria-label={`${label}: ${statusDescription}${
                  overdueWarning ? `. Warning: ${overdueWarning}` : ""
                }`}
                sx={{
                  minWidth: isDailyStock
                    ? { xs: 240, sm: 260, md: 0 }
                    : undefined,
                  minHeight: overdueWarning ? 94 : 76,
                }}
                label={
                  <Box sx={{ minWidth: 0, textAlign: "left" }}>
                    <Typography
                      component="span"
                      display="block"
                      fontWeight={800}
                      lineHeight={1.25}
                    >
                      {label}
                    </Typography>
                    <Typography
                      component="span"
                      display="block"
                      variant="caption"
                      color={
                        hasPendingToday
                          ? "warning.main"
                          : "text.secondary"
                      }
                      noWrap
                      title={statusDescription}
                    >
                      {statusDescription}
                    </Typography>
                    {overdueWarning && (
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.25,
                          color: "warning.dark",
                        }}
                        title={overdueWarning}
                      >
                        <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography
                          component="span"
                          variant="caption"
                          fontWeight={700}
                          noWrap
                        >
                          {overdueWarning}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Paper>
    </Box>
  );
};

export default InventoryHero;
